import React from 'react';
import { Modal, Spin } from 'antd';
import s from './s.module.less';
import LeftIcon from './imgs/left.png';
import LeftIcon2 from './imgs/left2.png';
import LeftIcon3 from './imgs/left3.png';
import LeftIcon4 from './imgs/left4.png';

import RightIcon from './imgs/right.svg';
import RightIcon2 from './imgs/right2.svg';
import RightIcon3 from './imgs/right3.svg';
import RightIcon4 from './imgs/right4.svg';

import Avatar from './imgs/avatar.png';
import useKanmonConnect from 'hooks/useKanmonConnect';
import { getKanmonUiView } from 'utils/kanmonUi';
import commonS from 'styles/common.module.less';
import FinancialsLandingPage from 'pages/dashboard/FinancialsPage/components/FinancialsLandingPage';
import {
    getHasAutoPopInvoicePaybackTermModal,
    setHasAutoPopInvoicePaybackTermModal,
    EKey,
    getHasClosePayLaterBannerInFinancials,
    getHasClosePayLaterBannerInDashboard,
    setHasClosePayLaterBannerInFinancials,
    setHasClosePayLaterBannerInDashboard,
} from 'utils/localstore';
import PaybackTermModal from 'pages/dashboard/FinancialsPage/components/PaybackTermModal';
import useKanmonProducts from 'hooks/useKanmonProducts';

//https://www.figma.com/design/w4vpLJZeMLx9XzMg0c71Dm/Financial---Settings?node-id=8104-79126&m=dev
//a. User has not applied Klarity Pay Later yet
//b. User's application is being processed
//c. User’s application is under review
//d. After the user’s application is approved
//e. If a user has clicked ‘View your offers’ & and opened the modal
//f. If the user’s application is declined

type Props = {
    closeMarkKey?: EKey.HAS_CLOSE_PAY_LATER_BANNER_IN_FINANCIALS | EKey.HAS_CLOSE_PAY_LATER_BANNER_IN_DASHBOARD;
};

const PayLaterBanner = (props: Props) => {
    const { closeMarkKey = EKey.HAS_CLOSE_PAY_LATER_BANNER_IN_FINANCIALS } = props;
    const [autoShowKanmonModal, setAutoShowKanmonModal] = React.useState(false);
    const [showPaylaterModal, setShowPaylaterModal] = React.useState(false);
    const [showPaybackTermModal, setShowPaybackTermModal] = React.useState(false);
    const [products, loadingProducts, , productsError] = useKanmonProducts();
    const [kanmonConnect, kanmonInfo, loadingKanmonConnect, refetchKanmonInfo, ui] = useKanmonConnect();
    const productsStatus = productsError ? 'error' : 'ready';
    const view = getKanmonUiView(ui, !!kanmonInfo?.kanmonBusinessId, products, loadingProducts ? 'loading' : productsStatus);
    const [isClosed, setIsClosed] = React.useState(
        closeMarkKey === EKey.HAS_CLOSE_PAY_LATER_BANNER_IN_FINANCIALS
            ? getHasClosePayLaterBannerInFinancials()
            : getHasClosePayLaterBannerInDashboard(),
    );

    React.useEffect(() => {
        if (autoShowKanmonModal && kanmonConnect) {
            setAutoShowKanmonModal(false);
            setShowPaylaterModal(false);
            kanmonConnect.show();
        }
    }, [autoShowKanmonModal, kanmonConnect]);

    React.useEffect(() => {
        if (view.canSelectPayLater && products.length > 0) {
            const hasAutoPopInvoicePaybackTermModal = getHasAutoPopInvoicePaybackTermModal();
            if (!hasAutoPopInvoicePaybackTermModal) {
                setShowPaybackTermModal(true);
                setHasAutoPopInvoicePaybackTermModal(true);
            }
        }
    }, [view.canSelectPayLater, products]);

    const visuals: Record<string, { className: string; icon: string; iconRight: string }> = {
        review: { className: s.wrapC, icon: LeftIcon2, iconRight: RightIcon2 },
        offers: { className: s.wrapD, icon: LeftIcon3, iconRight: RightIcon3 },
        servicing: { className: s.wrapE, icon: LeftIcon3, iconRight: RightIcon3 },
        unavailable: { className: s.wrapF, icon: LeftIcon4, iconRight: RightIcon4 },
    };
    const visual = visuals[view.phase];
    const wrapCls = `${s.wrap} ${visual?.className || ''}`;
    const btnCls = `${s.btn} ${view.showInputRequired ? s.actionRequiredBtn : ''}`;
    const icon = visual?.icon || LeftIcon;
    const iconRight = visual?.iconRight || RightIcon;

    if (isClosed) {
        return null;
    }

    if (loadingKanmonConnect && !kanmonInfo) {
        return (
            <div className={s.loading}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className={wrapCls}>
            <div className={s.left}>
                <img src={icon} alt="left" className={s.img} />
            </div>
            <div className={s.center}>
                <div className={s.title}>{view.titleText}</div>
                <div className={s.des}>{view.detailText}</div>
            </div>
            <div className={s.right}>
                <div className={s.rightBg}>
                    <img src={iconRight} alt="right" className={s.rightBgImg} />
                </div>
                <div className={s.rightInfo}>
                    <div className={s.avatar}>
                        <img src={Avatar} alt="avatar" className={s.avatarImg} />
                    </div>
                    <div className={s.amount}>{kanmonInfo?.joinedProviderCount ?? 0} providers joined</div>
                    <div
                        onClick={async () => {
                            if (view.primaryAction === 'apply') {
                                setShowPaylaterModal(true);
                            } else if (view.primaryAction === 'retry' || !kanmonConnect) {
                                refetchKanmonInfo();
                            } else {
                                kanmonConnect.show();
                            }
                        }}
                        className={btnCls}
                    >
                        {view.ctaText}
                    </div>
                </div>
                <span
                    onClick={() => {
                        setIsClosed(true);
                        if (closeMarkKey === EKey.HAS_CLOSE_PAY_LATER_BANNER_IN_FINANCIALS) {
                            setHasClosePayLaterBannerInFinancials(true);
                        } else {
                            setHasClosePayLaterBannerInDashboard(true);
                        }
                    }}
                    className={s.close}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path
                            d="M1 13L13 1M1 1L13 13"
                            stroke="#4B5563"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </span>
            </div>
            <Modal
                width="90%"
                open={showPaylaterModal}
                className={`${commonS.modalFixHeightWrap} ${s.modalWrap}`}
                title="Grow your practice faster with Klarity Pay Later (Powered by Kanmon)"
                onCancel={() => {
                    setShowPaylaterModal(false);
                }}
                footer={null}
                destroyOnClose
            >
                <div className={s.modalWrap}>
                    <FinancialsLandingPage
                        onClickedApplyNow={() => {
                            refetchKanmonInfo();
                            setAutoShowKanmonModal(true);
                        }}
                        doShowKanmonModal={() => {
                            setShowPaylaterModal(false);
                            if (kanmonConnect) {
                                kanmonConnect.show();
                            } else {
                                refetchKanmonInfo();
                            }
                        }}
                        isModal
                    />
                </div>
            </Modal>
            <PaybackTermModal
                open={showPaybackTermModal}
                onCancel={() => setShowPaybackTermModal(false)}
                onSuccess={() => {
                    setShowPaybackTermModal(false);
                }}
            />
        </div>
    );
};

export default PayLaterBanner;
